import { ChangeEvent, useRef, useState, useEffect } from "react";
import ReactCropper, { ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Trans } from "@lingui/react/macro";

interface ImageUploadWithCropProps {
  image?: string;
  onImageChange: (blob: Blob | undefined) => void;
  showCropper?: boolean;
  showClearImageButton?: boolean;
  maxSizeBytes?: number;
  aspectRatio?: number;
  renderPreview?: (
    imageUrl: string | undefined,
    isUploading: boolean,
  ) => React.ReactNode;
  isUploading?: boolean;
}

export const ImageUploadWithCrop = ({
  image,
  onImageChange,
  showCropper = false,
  showClearImageButton = true,
  maxSizeBytes = 200_000,
  aspectRatio = 1,
  renderPreview,
  isUploading = false,
}: ImageUploadWithCropProps) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImage, setLocalImage] = useState<string>("");
  const [hasCropped, setHasCropped] = useState(false);

  // Clean up when component unmounts or localImage changes
  useEffect(() => {
    return () => {
      if (localImage && localImage.startsWith("blob:")) {
        URL.revokeObjectURL(localImage);
      }
    };
  }, [localImage]);

  const onFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const files = event.target.files;
    if (files == null || files.length === 0) {
      return;
    }

    const file = files[0];

    // If cropper is not shown, process the image directly
    if (!showCropper) {
      const blob = await compressFileToMaxSize(file, maxSizeBytes);
      onImageChange(blob);
      return;
    }

    // Otherwise load it into the cropper
    const reader = new FileReader();
    reader.onload = () => {
      setLocalImage(reader.result as string);
      setHasCropped(false);
    };
    reader.readAsDataURL(file);
  };

  const onCrop = async () => {
    // Prevent repeated crops
    if (hasCropped) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) return;

    setHasCropped(true);
    const blob = await compressToMaxSize(canvas, maxSizeBytes);
    onImageChange(blob);
  };

  async function compressToMaxSize(
    canvas: HTMLCanvasElement,
    maxSize: number,
  ): Promise<Blob> {
    let quality = 0.9;
    let blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b as Blob), "image/jpeg", quality),
    );

    while (blob.size > maxSize && quality > 0.01) {
      quality -= 0.05;
      blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b as Blob), "image/jpeg", quality),
      );
    }
    return blob;
  }

  async function compressFileToMaxSize(
    file: File,
    maxSize: number,
  ): Promise<Blob> {
    // If file is already small enough, return as-is
    if (file.size <= maxSize) {
      return file;
    }

    // Otherwise, compress it
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = async () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);

        const compressedBlob = await compressToMaxSize(canvas, maxSize);
        resolve(compressedBlob);
      };

      reader.readAsDataURL(file);
    });
  }

  const handleClear = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (localImage && localImage.startsWith("blob:")) {
      URL.revokeObjectURL(localImage);
    }
    setLocalImage("");
    setHasCropped(false);
    onImageChange(undefined);
  };

  const hasImage = showCropper ? !!localImage : !!image;

  return (
    <div className="flex w-full flex-col items-center gap-4">
      {/* Preview */}
      {renderPreview && (
        <div className={`${isUploading ? "opacity-50" : ""}`}>
          {renderPreview(image, isUploading)}
        </div>
      )}

      {/* Cropper Section */}
      {showCropper && localImage && (
        <div className="mb-4 w-full">
          <ReactCropper
            style={{ maxHeight: "24rem" }}
            aspectRatio={aspectRatio}
            viewMode={2}
            guides={false}
            ref={cropperRef}
            src={localImage}
            cropend={onCrop}
            ready={onCrop}
          />
        </div>
      )}

      {/* File Upload Area */}
      {!hasImage && (
        <div className="flex w-full items-center justify-center">
          <label
            htmlFor="file-upload"
            className="border-soluud-primary bg-soluud-background hover:bg-soluud-info flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg
                className="text-soluud-primary mb-4 h-8 w-8"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="text-soluud-primary mb-2 text-sm">
                <span className="font-semibold">
                  <Trans>Click to select</Trans>
                </span>
              </p>
            </div>
            <input
              ref={fileInputRef}
              onChange={onFileInputChange}
              accept="image/png,image/jpg,image/jpeg"
              id="file-upload"
              type="file"
              className="hidden"
            />
          </label>
        </div>
      )}

      {hasImage && showClearImageButton && (
        <button
          onClick={handleClear}
          className="bg-soluud-primary hover:bg-soluud-primary-hover w-full cursor-pointer rounded-xl px-4 py-2 text-white transition"
        >
          <Trans>Clear Image</Trans>
        </button>
      )}
    </div>
  );
};
